import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import PeopleIcon from '@material-ui/icons/People';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import EditIcon from '@material-ui/icons/Edit';
import BarChartIcon from '@material-ui/icons/BarChart';

import { SOLUTION } from './solutions';
import { LEARNCREATOR_START_PATH } from '../../utils/constants';
import { COS_PREVIEW_PATH } from '../../utils/cpci.util';

/**
 * Teacher Workspace navigation (level 23).
 *
 * Structure and labels come from the PbDQ Navigation Bar sheet, rows 152-168.
 * The sheet publishes this workspace for P1B and LC only — "Only Published for
 * P1B and LC - Designated Teachers for Creating Programs" (row 150).
 *
 * `ctx` is { authUser, canAuthor } — canAuthor is the LearnCreator capability,
 * NOT the access level. A teacher without `create_learning_program` is still a
 * teacher, just one who cannot build or distribute.
 *
 * DIFFERENCES FROM SCHOOL, all from the sheet:
 *   · no Performance Summary
 *   · Insights holds the Program Impact Dashboard only (row 164), not the
 *     School Dashboard
 *   · no Government Checklist under Solutions
 */
export const TEACHER_WORKSPACE_NAV = [
    {
        code: 'TW_HO',
        /**
         * Tutorial step-1 anchor. REQUIRED — see the note on PW_HO. Losing this
         * id traps the user on Home and breaks every other sidebar item.
         */
        id: 'sidebar-home-link',
        label: 'Home',
        icon: HomeIcon,
        path: '/my_workspace',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        visibleWhen: () => true,
    },

    {
        code: 'TW_SO',
        label: 'Solutions',
        icon: CategoryIcon,
        path: '/dq_pro/solutions',
        slideCode: 'CS-Solution-Overview',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        visibleWhen: () => true,
        /**
         * Common Section — the same pages Partner and School show, reusing the
         * existing routes. Overview / Preview are Level 3 sidebar rows (the
         * sheet's Level 3 columns); COS Readiness Solution has no Preview.
         *
         * Row 154 gives Solutions its own "Overview" with no Level 2 code — the
         * code below follows the convention used in the other two workspaces.
         */
        children: [
            {
                code: 'TW_SO_OV', label: 'Overview', path: '/dq_pro/solutions/overview',
                slideCode: 'CS-Solution-Overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
            },
            {
                code: 'TW_SO_P1', label: 'COS Readiness Solution', path: '/dq_pro/solutions/readiness',
                slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                children: [
                    {
                        code: 'TW_SO_P1_OV', label: 'Overview', path: '/dq_pro/solutions/readiness/overview',
                        slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'TW_SO_P0', label: 'COS Children Assessment', path: '/dq_pro/solutions/children-assessment',
                slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                children: [
                    {
                        code: 'TW_SO_P0_OV', label: 'Overview', path: '/dq_pro/solutions/children-assessment/overview',
                        slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                    },
                    {
                        code: 'TW_SO_P0_PV', label: 'Preview', path: '/dq_pro/solutions/children-assessment/preview',
                        // The path above redirects to the COS catalogue preview page.
                        // Only that page — '/dq_pro/product/view' alone also claimed
                        // the preview of every built assessment.
                        matchPrefixes: [COS_PREVIEW_PATH],
                        slideCode: 'CS-Solution-P0-preview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'TW_SO_LC', label: 'LearnCreator', path: '/dq_pro/solutions/learncreator',
                slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                children: [
                    {
                        code: 'TW_SO_LC_OV', label: 'Overview', path: '/dq_pro/solutions/learncreator/overview',
                        slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                    },
                    {
                        code: 'TW_SO_LC_PV', label: 'Preview', path: '/dq_pro/solutions/learncreator/preview',
                        slideCode: 'CS-Solution-LC-preview', solutions: [SOLUTION.P1B, SOLUTION.LC], visibleWhen: () => true,
                    },
                ],
            },
        ],
    },

    {
        code: 'TW_MC',
        /** Tutorial anchor, and the Home page's FloatingNudge anchor. */
        id: 'sidebar-classes-link',
        label: 'My Classes',
        icon: PeopleIcon,
        path: '/dq_pro/classes',
        slideCode: 'TW-P1B, LC-My Classes-Main',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        // Sheet row 161 codes this SW_MC inside the TW block — a copy/paste slip.
        visibleWhen: () => true,
    },

    {
        code: 'TW_MP',
        id: 'sidebar-programs-link',
        label: 'My Programs',
        icon: EmojiObjectsIcon,
        path: '/dq_pro/learning/program/list',
        slideCode: 'TW-P1B, LC-MyPrograms',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        visibleWhen: () => true,
    },

    {
        code: 'TW_LC',
        id: 'sidebar-learncreator-link',
        label: 'LearnCreator',
        icon: EditIcon,
        // The LearnCreator landing page, which every entry point opens; typing
        // a goal there starts the builder (bug sheet Ian-LC-30Sep-1). NOT
        // /dq_pro/home: that page is a guest/marketing teaser of the same idea
        // and always redirects an already-logged-in, non-guest user away.
        path: LEARNCREATOR_START_PATH,
        slideCode: 'TW-P1B, LC-LearnCreator',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        /**
         * The LearnCreator CAPABILITY, not the level. PrivateRouteWithLayout's
         * lcOnlyPrefixes matches this path via its '/dq_pro/learncreator'
         * prefix (and the builder via '/dq_pro/program/creation'), so a plain
         * teacher cannot reach either by URL — menu and guard agree.
         */
        visibleWhen: (ctx) => ctx.canAuthor,
    },

    {
        code: 'TW_IN',
        label: 'Insights',
        icon: BarChartIcon,
        path: '/dq_pro/program-impact-dashboard',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        /**
         * Holds only the Program Impact Dashboard, which is an LC-Teacher
         * capability — so a plain teacher has nothing here and the whole group
         * is hidden rather than opening onto an empty list.
         *
         * No Class Dashboard row: the sheet lists only TW_IN_CD "Program Impact
         * Dashboard" under TW_IN (row 164), and a single class-dashboard link
         * has no correct destination anyway — that page is scoped to one class
         * through a `section_code` param. Each class in My Classes already opens
         * its own dashboard with the code attached.
         */
        visibleWhen: (ctx) => ctx.canAuthor,
        children: [
            {
                code: 'TW_IN_CD',
                id: 'sidebar-pid-link',
                label: 'Program Impact Dashboard',
                path: '/dq_pro/program-impact-dashboard',
                // The sheet codes this TW_IN_CD but names it "Program Impact
                // Dashboard" — CD reads as Class Dashboard by convention, so the
                // code and the name disagree. Code kept verbatim for traceability;
                // the NAME is the one that matches the page it opens.
                slideCode: 'TW-P1B, LC-Insights-Program Impact Dashboard',
                solutions: [SOLUTION.P1B, SOLUTION.LC],
                visibleWhen: (ctx) => ctx.canAuthor,
            },
        ],
    },

    /*
     * NO Support row.
     *
     * The Teacher block of the sheet lists no Support — and unlike School it has
     * no current-sidebar column either, so there is not even a "Keep" note to
     * weigh. Partner is the only workspace given a Support code (PW_SP).
     */
];
